import React, { useState, useEffect } from "react";
import { Button, Form } from "react-bootstrap";
import { withTranslation } from "react-i18next";
import { BiX } from "react-icons/bi";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "../Forms.scss";

const PARENT_STATUS_OPTIONS = [
	{ value: "COMPLETED", labelKey: "Completed.label" },
	{ value: "IN_PROGRESS", labelKey: "InProgress.label" },
	{ value: "NOT_STARTED", labelKey: "NotStarted.label" }
];

// Status options for Responses and Approval tabs
const CHILD_STATUS_OPTIONS_RESPONSES_APPROVAL = [
	{ value: "DRAFT", labelKey: "NotStarted.label" },
	{ value: "IN_PROGRESS", labelKey: "InProgress.label" },
	{ value: "PENDING_APPROVAL_LEVEL_1", labelKey: "PendingApproval1st.label" },
	{ value: "PENDING_APPROVAL_LEVEL_2", labelKey: "PendingApproval2nd.label" },
	{ value: "PENDING_APPROVAL_LEVEL_3", labelKey: "PendingApproval3rd.label" },
	{ value: "CORRECTION_PENDING", labelKey: "CorrectionPending.label" },
	{ value: "COMPLETED", labelKey: "Completed.label" },
	// { value: "PENDING_APPROVAL", labelKey: "PendingApproval.label" }
];

// Status options for Summary tab
const CHILD_STATUS_OPTIONS_SUMMARY = [
	{ value: "NOT_STARTED", labelKey: "NotStarted.label" },
	{ value: "IN_PROGRESS", labelKey: "InProgress.label" },
	{ value: "COMPLETED", labelKey: "Completed.label" }
];

function AssessmentFilterPanel({ t, onApplyFilter, onResetFilter, onFilterHide, initialFilters = {}, activeTab = 'summary' }) {
	const [parentStatusFilters, setParentStatusFilters] = useState({
		COMPLETED: initialFilters.parentStatusFilters?.COMPLETED ?? false,
		IN_PROGRESS: initialFilters.parentStatusFilters?.IN_PROGRESS ?? false,
		NOT_STARTED: initialFilters.parentStatusFilters?.NOT_STARTED ?? false
	});

	// Determine which child status options to use based on active tab
	const childStatusOptions = activeTab === 'summary' ? CHILD_STATUS_OPTIONS_SUMMARY : CHILD_STATUS_OPTIONS_RESPONSES_APPROVAL;
	
	// Initialize child status filters based on active tab
	const getInitialChildStatusFilters = () => {
		if (activeTab === 'summary') {
			return {
				NOT_STARTED: initialFilters.childStatusFilters?.NOT_STARTED ?? false,
				IN_PROGRESS: initialFilters.childStatusFilters?.IN_PROGRESS ?? false,
				COMPLETED: initialFilters.childStatusFilters?.COMPLETED ?? false
			};
		} else {
			return {
				DRAFT: initialFilters.childStatusFilters?.DRAFT ?? false,
				IN_PROGRESS: initialFilters.childStatusFilters?.IN_PROGRESS ?? false,
				PENDING_APPROVAL_LEVEL_1: initialFilters.childStatusFilters?.PENDING_APPROVAL_LEVEL_1 ?? false,
				PENDING_APPROVAL_LEVEL_2: initialFilters.childStatusFilters?.PENDING_APPROVAL_LEVEL_2 ?? false,
				PENDING_APPROVAL_LEVEL_3: initialFilters.childStatusFilters?.PENDING_APPROVAL_LEVEL_3 ?? false,
				CORRECTION_PENDING: initialFilters.childStatusFilters?.CORRECTION_PENDING ?? false,
				COMPLETED: initialFilters.childStatusFilters?.COMPLETED ?? false,
				// PENDING_APPROVAL: initialFilters.childStatusFilters?.PENDING_APPROVAL ?? false
			};
		}
	};

	const [childStatusFilters, setChildStatusFilters] = useState(getInitialChildStatusFilters());

	const [submittedDate, setSubmittedDate] = useState(
		initialFilters.submittedDate ? new Date(initialFilters.submittedDate) : null
	);

	// Sync parent status filters when initialFilters changes
	useEffect(() => {
		setParentStatusFilters({
			COMPLETED: initialFilters.parentStatusFilters?.COMPLETED ?? false,
			IN_PROGRESS: initialFilters.parentStatusFilters?.IN_PROGRESS ?? false,
			NOT_STARTED: initialFilters.parentStatusFilters?.NOT_STARTED ?? false
		});
	}, [initialFilters.parentStatusFilters]);

	// Reset child status filters when activeTab changes or initialFilters changes
	useEffect(() => {
		if (activeTab === 'summary') {
			setChildStatusFilters({
				NOT_STARTED: initialFilters.childStatusFilters?.NOT_STARTED ?? false,
				IN_PROGRESS: initialFilters.childStatusFilters?.IN_PROGRESS ?? false,
				COMPLETED: initialFilters.childStatusFilters?.COMPLETED ?? false
			});
		} else {
			setChildStatusFilters({
				DRAFT: initialFilters.childStatusFilters?.DRAFT ?? false,
				IN_PROGRESS: initialFilters.childStatusFilters?.IN_PROGRESS ?? false,
				PENDING_APPROVAL_LEVEL_1: initialFilters.childStatusFilters?.PENDING_APPROVAL_LEVEL_1 ?? false,
				PENDING_APPROVAL_LEVEL_2: initialFilters.childStatusFilters?.PENDING_APPROVAL_LEVEL_2 ?? false,
				PENDING_APPROVAL_LEVEL_3: initialFilters.childStatusFilters?.PENDING_APPROVAL_LEVEL_3 ?? false,
				CORRECTION_PENDING: initialFilters.childStatusFilters?.CORRECTION_PENDING ?? false,
				COMPLETED: initialFilters.childStatusFilters?.COMPLETED ?? false,
				// PENDING_APPROVAL: initialFilters.childStatusFilters?.PENDING_APPROVAL ?? false
			});
		}
	}, [activeTab, initialFilters.childStatusFilters]);

	// Sync submitted date when initialFilters changes
	useEffect(() => {
		setSubmittedDate(
			initialFilters.submittedDate ? new Date(initialFilters.submittedDate) : null
		);
	}, [initialFilters.submittedDate]);

	const handleParentStatusChange = (status) => {
		setParentStatusFilters(prev => ({
			...prev,
			[status]: !prev[status]
		}));
	};

	const handleChildStatusChange = (status) => {
		setChildStatusFilters(prev => ({
			...prev,
			[status]: !prev[status]
		}));
	};

	const handleParentSelectAll = () => {
		const allSelected = Object.values(parentStatusFilters).every(val => val);
		setParentStatusFilters({
			COMPLETED: !allSelected,
			IN_PROGRESS: !allSelected,
			NOT_STARTED: !allSelected
		});
	};

	const handleChildSelectAll = () => {
		const allSelected = Object.values(childStatusFilters).every(val => val);
		const newFilters = {};
		childStatusOptions.forEach(option => {
			newFilters[option.value] = !allSelected;
		});
		setChildStatusFilters(newFilters);
	};

	const applyFilter = () => {
		const selectedParentStatuses = Object.entries(parentStatusFilters)
			.filter(([_, selected]) => selected)
			.map(([status]) => status);

		const selectedChildStatuses = Object.entries(childStatusFilters)
			.filter(([_, selected]) => selected)
			.map(([status]) => status);

		onApplyFilter({
			parentStatusFilters: parentStatusFilters,
			childStatusFilters: childStatusFilters,
			parentStatus: selectedParentStatuses.length > 0 ? selectedParentStatuses.join(",") : null,
			childStatus: selectedChildStatuses.length > 0 ? selectedChildStatuses.join(",") : null,
			submittedDate: submittedDate ? submittedDate.toISOString() : null
		});
	};

	const resetFilter = () => {
		setParentStatusFilters({
			COMPLETED: false,
			IN_PROGRESS: false,
			NOT_STARTED: false
		});
		const resetChildFilters = {};
		childStatusOptions.forEach(option => {
			resetChildFilters[option.value] = false;
		});
		setChildStatusFilters(resetChildFilters);
		setSubmittedDate(null);
		onResetFilter();
	};

	const allParentSelected = Object.values(parentStatusFilters).every(val => val);
	const allChildSelected = Object.values(childStatusFilters).every(val => val);

	return (
		<div className="AssessmentFilterPanel_Eventcard">
			<div className="AssessmentFilterPanel_Eventcard_head">
				<h5>{t("Filter.label")}</h5>
				<div 
					className="btnClose" 
					onClick={() => onFilterHide(false)}>
					<BiX />
				</div>
			</div>
			<div className="AssessmentFilterPanel_Eventcard_body">
				<div className="row m-0">
					{/* Show Parent Status section only for Summary tab */}
					{activeTab === 'summary' && (
						<div className="col-12 p-2">
							<div className="mb-3">
								<h6 className="mb-2">{t("AssessmentFilterPanel.statusOfParentEntities.label")}</h6>
								<Form.Check
									type="checkbox"
									id="parent-status-checkbox-all"
									label={allParentSelected ? t("DeselectAll.label") : t("SelectAll.label")}
									checked={allParentSelected}
									onChange={handleParentSelectAll}
									className="mb-1"
								/>
								{PARENT_STATUS_OPTIONS.map((option) => (
									<Form.Check
										key={option.value}
										type="checkbox"
										id={`parent-status-${option.value}`}
										label={t(option.labelKey)}
										checked={parentStatusFilters[option.value]}
										onChange={() => handleParentStatusChange(option.value)}
									/>
								))}
							</div>
						</div>
					)}
					{/* Show Child Status section for Responses and Approval tabs */}
					{(activeTab === 'responses' || activeTab === 'approvals') && (
						<div className="col-12 p-2">
							<div className="mb-3">
								<h6 className="mb-2">{t("Status.label")}</h6>
								<Form.Check
									type="checkbox"
									id="child-status-checkbox-all"
									label={allChildSelected ? t("DeselectAll.label") : t("SelectAll.label")}
									checked={allChildSelected}
									onChange={handleChildSelectAll}
									className="mb-1"
								/>
								{childStatusOptions.map((option) => (
									<Form.Check
										key={option.value}
										type="checkbox"
										id={`child-status-${option.value}`}
										label={t(option.labelKey)}
										checked={childStatusFilters[option.value] || false}
										onChange={() => handleChildStatusChange(option.value)}
									/>
								))}
							</div>
						</div>
					)}
					{/* <div className="col-12 p-2">
						<div className="mb-3">
							<h6 className="mb-2">{t("SubmittedDate.label")}</h6>
							<ReactDatePicker
								selected={submittedDate}
								onChange={(date) => setSubmittedDate(date)}
								dateFormat="MM/dd/yyyy"
								className="form-control"
								placeholderText={t("AssessmentFilterPanel.selectDate.label")}
								isClearable
							/>
						</div>
					</div> */}
				</div>
			</div>
			<div className="AssessmentFilterPanel_Eventcard_footer p-2 pb-3 text-center">
				<Button variant="primary"
					onClick={applyFilter}
					className="sbmBtn px-4">
					{t("Apply.label")}
				</Button>
				<Button variant="secondary"
					onClick={resetFilter}
					className="px-4 mx-2">
					{t("Reset.label")}
				</Button>
			</div>
		</div>
	);
}

AssessmentFilterPanel.propTypes = {};

export default withTranslation()(AssessmentFilterPanel);

